import { useRef, useState, useMemo, useCallback, useEffect } from "react";

// Renders untrusted email HTML inside a sandboxed iframe so its CSS/HTML can never
// leak into the surrounding app (nav, sidebar, etc.). The iframe auto-sizes to its
// content. `allow-same-origin` (WITHOUT `allow-scripts`) keeps email scripts from
// running while still letting us measure the content height.
export function EmailHtmlFrame({ html, testid = "email-body-frame", className = "" }) {
  const iframeRef = useRef(null);
  const observerRef = useRef(null);
  const [height, setHeight] = useState(120);

  const srcDoc = useMemo(
    () =>
      `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank">` +
      `<style>` +
      `html,body{margin:0;padding:0;background:transparent;}` +
      `body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;` +
      `font-size:14px;line-height:1.5;color:#374151;word-break:break-word;overflow-wrap:anywhere;overflow-x:hidden;}` +
      `img{max-width:100%;height:auto;}table{max-width:100%;}a{color:#2563eb;}` +
      `</style></head><body>${html || ""}</body></html>`,
    [html]
  );

  const measure = useCallback(() => {
    const f = iframeRef.current;
    if (!f) return;
    try {
      const doc = f.contentDocument || f.contentWindow?.document;
      if (doc && doc.body) {
        const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
        if (h > 0) setHeight(h + 8);
      }
    } catch (e) {
      /* opaque origin — cannot measure, keep fallback height */
    }
  }, []);

  const handleLoad = useCallback(() => {
    measure();
    const f = iframeRef.current;
    try {
      const doc = f?.contentDocument || f?.contentWindow?.document;
      if (doc) {
        // Re-measure as images finish loading (email images load async).
        doc.querySelectorAll("img").forEach((img) => {
          if (!img.complete) img.addEventListener("load", measure, { once: true });
        });
        if ("ResizeObserver" in window && doc.body) {
          observerRef.current?.disconnect();
          observerRef.current = new ResizeObserver(() => measure());
          observerRef.current.observe(doc.body);
        }
      }
    } catch (e) {
      /* ignore */
    }
    // Fallback late re-measures for slow images/fonts.
    setTimeout(measure, 300);
    setTimeout(measure, 1200);
  }, [measure]);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return (
    <iframe
      ref={iframeRef}
      title="Email content"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={srcDoc}
      onLoad={handleLoad}
      data-testid={testid}
      className={className}
      style={{ width: "100%", border: 0, height: `${height}px`, display: "block", overflow: "hidden" }}
    />
  );
}

export default EmailHtmlFrame;
